/**
 * AI-assisted relationship discovery with a LIVE DEBUGGER (ADR-0026 §4).
 *
 * Targeted at ONE dataset (minimise AI cost), this streams the proposal and
 * shows the operator exactly what's happening: the context being assembled
 * (schema + L2 semantics, no rows), the model's thinking + raw output as it
 * arrives, and each candidate validated or rejected (with the reason). The kept
 * candidates are bulk-created like the deterministic discover dialog.
 *
 * The stream is SSE-over-POST (`lib/relationshipsAi.streamProposeAi`) — read by
 * hand, not via EventSource (which can't POST + carry the bearer token).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Bug, Loader2, Sparkles } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { datasetsApi } from "@/lib/api-datasets";
import {
  CARDINALITY_GLYPH,
  relationshipsApi,
  type AiRelationshipCandidate,
} from "@/lib/api-relationships";
import { streamProposeAi, type RelAiEvent } from "@/lib/relationshipsAi";

function candKey(c: AiRelationshipCandidate): string {
  return `${c.from_dataset_id}:${c.from_field}->${c.to_dataset_id}:${c.to_field}`;
}

/** One line in the debugger console. */
interface LogLine {
  tone: "step" | "context" | "rejected" | "usage" | "error";
  text: string;
}

const CONFIDENCE_TONE: Record<AiRelationshipCandidate["confidence"], "active" | "neutral" | "warning"> = {
  high: "active",
  medium: "neutral",
  low: "warning",
};

/** A proposal the model made that failed schema validation, kept visible so the
 *  operator sees WHY nothing (or less) was created. */
interface Rejected {
  text: string;
  reason: string;
}

const REJECT_REASON: Record<string, string> = {
  from_field_not_in_target: "campo di partenza inesistente",
  to_field_not_in_target_dataset: "campo di destinazione inesistente",
  unknown_target_dataset: "dataset di destinazione non valido",
};

export function ProposeAiRelationshipsDialog({
  open,
  onOpenChange,
  datasetId: fixedDatasetId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Preselect (and lock) the target dataset when launched from its context. */
  datasetId?: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [datasetId, setDatasetId] = React.useState<string | null>(fixedDatasetId ?? null);
  const [thinking, setThinking] = React.useState(false);
  const [running, setRunning] = React.useState(false);
  const [finished, setFinished] = React.useState(false);
  const [log, setLog] = React.useState<LogLine[]>([]);
  const [output, setOutput] = React.useState("");
  const [thoughts, setThoughts] = React.useState("");
  const [candidates, setCandidates] = React.useState<AiRelationshipCandidate[]>([]);
  const [rejected, setRejected] = React.useState<Rejected[]>([]);
  const [picked, setPicked] = React.useState<Set<string>>(() => new Set());
  const abortRef = React.useRef<AbortController | null>(null);
  const consoleRef = React.useRef<HTMLDivElement>(null);

  const datasetsQ = useQuery({
    queryKey: ["datasets", "list"],
    queryFn: () => datasetsApi.list(),
    enabled: open && !fixedDatasetId,
  });

  // Reset everything when the dialog (re)opens.
  React.useEffect(() => {
    if (open) {
      setDatasetId(fixedDatasetId ?? null);
      setThinking(false);
      reset();
    }
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fixedDatasetId]);

  // Keep the console pinned to the latest output (scrollTop, not scrollTo — the
  // latter is unimplemented in jsdom and would throw in tests).
  React.useEffect(() => {
    if (consoleRef.current) consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
  }, [output, thoughts, log]);

  function reset() {
    setRunning(false);
    setFinished(false);
    setLog([]);
    setOutput("");
    setThoughts("");
    setCandidates([]);
    setRejected([]);
    setPicked(new Set());
  }

  function onEvent(ev: RelAiEvent) {
    switch (ev.type) {
      case "step":
        setLog((l) => [...l, { tone: "step", text: ev.label }]);
        break;
      case "context":
        setLog((l) => [
          ...l,
          {
            tone: "context",
            text: t("relationships.proposeAi.contextLine", {
              defaultValue:
                "{{fields}} campi · {{catalog}} dataset in catalogo · {{existing}} relazioni esistenti",
              fields: ev.field_count,
              catalog: ev.catalog_count,
              existing: ev.existing_count,
            }),
          },
        ]);
        break;
      case "thinking":
        setThoughts((s) => s + ev.text);
        break;
      case "delta":
        setOutput((s) => s + ev.text);
        break;
      case "candidate":
        setCandidates((c) => [...c, ev.candidate]);
        setPicked((p) => new Set(p).add(candKey(ev.candidate)));
        break;
      case "rejected": {
        const where = `${String(ev.raw.from_field ?? "?")} → ${String(
          ev.raw.to_dataset ?? "?",
        )}.${String(ev.raw.to_field ?? "?")}`;
        const why = REJECT_REASON[ev.reason] ?? ev.reason;
        setLog((l) => [...l, { tone: "rejected", text: `✗ ${where} — ${why}` }]);
        setRejected((r) => [...r, { text: where, reason: why }]);
        break;
      }
      case "error":
        setLog((l) => [...l, { tone: "error", text: ev.message }]);
        break;
      case "final":
        if (ev.usage?.output_tokens != null) {
          setLog((l) => [
            ...l,
            {
              tone: "usage",
              text: t("relationships.proposeAi.usageLine", {
                defaultValue: "{{out}} token generati ({{in}} in input)",
                out: ev.usage.output_tokens,
                in: ev.usage.input_tokens ?? 0,
              }),
            },
          ]);
        }
        break;
      case "done":
        setRunning(false);
        setFinished(true);
        break;
    }
  }

  async function start() {
    if (!datasetId) return;
    reset();
    setRunning(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      await streamProposeAi(datasetId, { thinking }, onEvent, ctrl.signal);
    } catch {
      if (!ctrl.signal.aborted) onEvent({ type: "error", message: "stream_failed" });
    } finally {
      setRunning(false);
      setFinished(true);
    }
  }

  const create = useMutation({
    mutationFn: async () => {
      const chosen = candidates.filter((c) => picked.has(candKey(c)));
      await Promise.all(
        chosen.map((c) =>
          relationshipsApi.create({
            name: c.name,
            from_dataset_id: c.from_dataset_id,
            from_field: c.from_field,
            to_dataset_id: c.to_dataset_id,
            to_field: c.to_field,
            cardinality: c.cardinality,
          }),
        ),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["relationships"] });
      onOpenChange(false);
    },
  });

  const toggle = (key: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const datasetOptions = (datasetsQ.data ?? []).map((d) => ({
    value: d.id,
    label: d.name,
    description: d.binding,
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm font-semibold">
            <Sparkles className="h-4 w-4" strokeWidth={1.5} />
            {t("relationships.proposeAi.title", "Proponi relazioni con AI")}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="mb-3 text-xs text-muted-foreground">
            {t(
              "relationships.proposeAi.body",
              "Scegli il dataset di cui mappare le relazioni: l'AI cerca collegamenti DA questo dataset verso gli altri, usando schema + semantica (nessun dato letto). Una alla volta, per contenere i costi AI. Segui il ragionamento in tempo reale.",
            )}
          </p>

          {/* Controls */}
          <div className="mb-3 flex flex-wrap items-end gap-3">
            {!fixedDatasetId && (
              <div className="min-w-[16rem] flex-1">
                <label className="mb-1 block text-[11px] font-medium text-muted-foreground">
                  {t("relationships.proposeAi.dataset", "Dataset")}
                </label>
                <Combobox
                  options={datasetOptions}
                  value={datasetId}
                  onChange={setDatasetId}
                  loading={datasetsQ.isLoading}
                  disabled={running}
                  placeholder={t("relationships.proposeAi.pickDataset", "Scegli un dataset…")}
                />
              </div>
            )}
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <Switch checked={thinking} onCheckedChange={setThinking} disabled={running} />
              {t("relationships.proposeAi.thinking", "Ragionamento esteso")}
            </label>
            <Button size="sm" onClick={start} disabled={!datasetId || running}>
              {running ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              ) : (
                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
              )}
              {running
                ? t("relationships.proposeAi.running", "Analisi…")
                : t("relationships.proposeAi.start", "Avvia")}
            </Button>
          </div>

          {/* Debugger console */}
          {(running || finished) && (
            <div
              ref={consoleRef}
              className="mb-3 max-h-56 overflow-auto rounded-lg border border-border/60 bg-surface/60 p-3 font-mono text-[11px] leading-relaxed"
            >
              <div className="mb-1 flex items-center gap-1.5 text-muted-foreground">
                <Bug className="h-3.5 w-3.5" strokeWidth={1.5} />
                {t("relationships.proposeAi.debugger", "Debugger")}
              </div>
              {log.map((line, i) => (
                <div
                  key={i}
                  className={
                    line.tone === "error"
                      ? "text-danger"
                      : line.tone === "rejected"
                        ? "text-warning"
                        : line.tone === "step"
                          ? "text-foreground"
                          : "text-muted-foreground"
                  }
                >
                  {line.tone === "step" ? "▸ " : "  "}
                  {line.text}
                </div>
              ))}
              {thoughts && (
                <div className="mt-1 whitespace-pre-wrap italic text-muted-foreground">{thoughts}</div>
              )}
              {output && <div className="mt-1 whitespace-pre-wrap text-muted-foreground/80">{output}</div>}
              {running && <span className="inline-block animate-pulse text-accent">▍</span>}
            </div>
          )}

          {/* Candidates */}
          {candidates.length > 0 && (
            <ul className="max-h-[40vh] divide-y divide-border/60 overflow-auto rounded-lg border border-border/60">
              {candidates.map((c) => {
                const key = candKey(c);
                return (
                  <li key={key} className="flex items-center gap-2 px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      checked={picked.has(key)}
                      onChange={() => toggle(key)}
                      className="h-3.5 w-3.5 accent-[hsl(var(--accent))]"
                    />
                    <span className="truncate">
                      <span className="text-muted-foreground">{c.from_dataset_name}</span>
                      <span className="font-mono text-xs">.{c.from_field}</span>
                    </span>
                    <Badge tone="neutral" className="text-[10px]">
                      {CARDINALITY_GLYPH[c.cardinality]}
                    </Badge>
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
                    <span className="truncate">
                      <span className="text-muted-foreground">{c.to_dataset_name}</span>
                      <span className="font-mono text-xs">.{c.to_field}</span>
                    </span>
                    <Badge tone={CONFIDENCE_TONE[c.confidence]} className="ml-auto text-[10px]" title={c.rationale}>
                      {c.confidence}
                    </Badge>
                  </li>
                );
              })}
            </ul>
          )}

          {finished && candidates.length === 0 && rejected.length === 0 && (
            <p className="py-6 text-center text-xs text-muted-foreground">
              {t("relationships.proposeAi.none", "Nessuna relazione proposta.")}
            </p>
          )}

          {/* Why some/all proposals weren't created — makes "Crea 0" legible. */}
          {finished && rejected.length > 0 && (
            <div className="mt-3 rounded-lg border border-warning/30 bg-warning/5 p-3">
              <p className="mb-1.5 text-[11px] font-medium text-warning">
                {t("relationships.proposeAi.rejectedTitle", "{{n}} proposte scartate", {
                  n: rejected.length,
                })}
                {candidates.length === 0 &&
                  ` — ${t("relationships.proposeAi.rejectedHint", "il modello ha indicato campi non presenti nello schema")}`}
              </p>
              <ul className="space-y-0.5 text-[11px] text-muted-foreground">
                {rejected.map((r, i) => (
                  <li key={i} className="flex items-center gap-1.5">
                    <span className="font-mono">{r.text}</span>
                    <span className="text-muted-foreground/70">· {r.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {create.isError && (
            <p className="mt-2 text-xs text-danger">{(create.error as Error).message}</p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Annulla")}
          </Button>
          <Button
            size="sm"
            disabled={picked.size === 0 || create.isPending || running}
            onClick={() => create.mutate()}
          >
            {create.isPending && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />}
            {t("relationships.proposeAi.create", "Crea {{n}}", { n: picked.size })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
